import { useMemo } from "react";

import { DOTS } from "../constants";
import { range } from "../utils";

export function usePaginationRange({
  count,
  pageSize,
  siblingCount = 1,
  pageNo,
}: {
  count: number;
  pageSize: number;
  siblingCount: number;
  pageNo: number;
}): (number | typeof DOTS)[] {
  return useMemo(() => {
    const totalPageCount = Math.ceil(count / pageSize);
    const totalPageNumbers = siblingCount + 5;

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(pageNo - siblingCount, 1);
    const rightSiblingIndex = Math.min(pageNo + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = range(1, 3 + 2 * siblingCount);
      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = range(
        totalPageCount - (3 + 2 * siblingCount) + 1,
        totalPageCount
      );
      return [1, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [1, DOTS, ...middleRange, DOTS, totalPageCount];
    }

    return range(1, totalPageCount);
  }, [count, pageSize, siblingCount, pageNo]);
}
